import type { DropdownProps } from '../../Dropdown';
import type { I18nLanguage } from '@readme/iso';

import { i18nLanguageLabels, i18nLanguages } from '@readme/iso';
import React, { useCallback, useState } from 'react';

import useClassy from '@core/hooks/useClassy';

import type { ButtonProps } from '@ui/Button';
import Button from '@ui/Button';
import Icon from '@ui/Icon';

import Dropdown from '../../Dropdown';
import Menu, { MenuDivider } from '../../Menu';
import MenuItem from '../../Menu/Item';

import classes from './style.module.scss';

export type LangPickerProps = Pick<
  ButtonProps,
  | 'circular'
  | 'disabled'
  | 'dropdown'
  | 'fullWidth'
  | 'ghost'
  | 'is'
  | 'kind'
  | 'link'
  | 'loading'
  | 'outline'
  | 'size'
  | 'target'
  | 'text'
  | 'to'
  | 'type'
  | 'uppercase'
> &
  Pick<DropdownProps, 'align' | 'justify' | 'open'> & {
    buttonClassName?: string;
    children?: React.ReactNode;
    className?: string;
    /**
     * Array of language short codes to show in the picker. Defaults to all supported languages.
     */
    enabledLanguages?: I18nLanguage[];
    /**
     * Choose between the globe, i18n, or translate glyphs.
     */
    glyph?: 'globe' | 'i18n' | 'translate';
    /**
     * Choose how to show or hide the icon.
     */
    icon?: 'first' | 'last' | 'none' | 'only';
    /**
     * Set the initial language selection by short code.
     */
    initialLanguage?: I18nLanguage;
    /**
     * Show the full language name or just the short code.
     */
    label?: 'long' | 'native' | 'short';
    /**
     * Handler called with the selected short code when the user chooses a new language.
     */
    onChange?: (lang: I18nLanguage) => void;
    style?: React.CSSProperties;
  };

const getLabel = (lang: string, labelType: 'long' | 'native' | 'short' = 'native') => {
  const labelKey = labelType === 'native' ? 'labelNative' : 'label';
  return i18nLanguageLabels[lang]?.[labelKey] || lang;
};

export default function LangPicker({
  align = 'bottom',
  buttonClassName,
  children,
  className,
  enabledLanguages = [...i18nLanguages],
  glyph = 'globe',
  icon = 'first',
  initialLanguage = 'en',
  justify = 'start',
  label: labelType = 'long',
  onChange,
  open,
  style = {},
  ...buttonProps
}: LangPickerProps) {
  const bem = useClassy(classes, 'LangPicker');

  const [selected, setSelected] = useState(initialLanguage);

  const handleChange = useCallback(
    (lang: I18nLanguage) => {
      setSelected(lang);
      onChange?.(lang);
    },
    [onChange],
  );

  return (
    <Dropdown
      align={align}
      className={`${bem()} ${className}`}
      clickInToClose={true}
      fullWidth={buttonProps.fullWidth}
      justify={justify}
      open={open}
      style={style}
      trigger="click"
    >
      <Button circular={icon === 'only'} uppercase={false} {...buttonProps} className={buttonClassName} role="button">
        {['first', 'only'].includes(icon) && <Icon name={glyph} />}
        {icon !== 'only' &&
          (labelType === 'short' ? (
            <span className={bem('-short-label')}>{selected.toUpperCase()}</span>
          ) : (
            getLabel(selected, labelType)
          ))}
        {icon === 'last' && <Icon name={glyph} />}
      </Button>
      <Menu>
        {enabledLanguages.map?.(lang => (
          <MenuItem
            key={lang}
            active={selected === lang}
            disabled={selected === lang}
            onClick={() => handleChange(lang)}
          >
            {getLabel(lang, labelType)}
          </MenuItem>
        ))}
        {!!children && (
          <>
            <MenuDivider />
            {children}
          </>
        )}
      </Menu>
    </Dropdown>
  );
}
